import { shouldExclude } from "./junkFilter";
import type { UploadPlan, UploadPlanFile } from "@titancloud/shared";

/**
 * Convert a FileList (from <input type="file">) into an UploadPlan.
 */
export function scanFileList(fileList: FileList): UploadPlan {
  const allFiles: UploadPlanFile[] = [];

  for (const file of Array.from(fileList)) {
    // webkitRelativePath is populated when the input has the `webkitdirectory`
    // attribute (folder picker); falls back to the bare filename otherwise.
    const relativePath = file.webkitRelativePath || file.name;
    const filter = shouldExclude(relativePath);
    allFiles.push({
      file,
      relativePath,
      sizeBytes: file.size,
      isJunk: filter.isJunk,
      ...(filter.reason !== undefined ? { junkReason: filter.reason } : {}),
    });
  }

  const included = allFiles.filter((f) => !f.isJunk);
  const excluded = allFiles.filter((f) => f.isJunk);

  return {
    files: allFiles,
    totalFiles: allFiles.length,
    includedFiles: included.length,
    excludedFiles: excluded.length,
    totalBytes: allFiles.reduce((acc, f) => acc + f.sizeBytes, 0),
    includedBytes: included.reduce((acc, f) => acc + f.sizeBytes, 0),
    savedBytes: excluded.reduce((acc, f) => acc + f.sizeBytes, 0),
  };
}

/**
 * Scan a DataTransfer containing dropped files/folders.
 * Uses the FileSystem Access API to traverse directories recursively.
 * Returns an UploadPlan with kept and excluded files.
 */
export async function scanDroppedItems(
  dataTransfer: DataTransfer
): Promise<UploadPlan> {
  // DataTransfer.items is cleared after the drop event's synchronous phase.
  // Collect all entries/files SYNCHRONOUSLY first, then process async.
  const collected: Array<{ entry: FileSystemEntry } | { file: File }> = [];
  for (const item of Array.from(dataTransfer.items)) {
    const entry = item.webkitGetAsEntry?.();
    if (entry) {
      collected.push({ entry });
    } else {
      const file = item.getAsFile();
      if (file) collected.push({ file });
    }
  }

  const allFiles: UploadPlanFile[] = [];
  for (const item of collected) {
    if ("entry" in item) {
      await traverseEntry(item.entry, "", allFiles);
    } else {
      const filter = shouldExclude(item.file.name);
      allFiles.push({
        file: item.file,
        relativePath: item.file.name,
        sizeBytes: item.file.size,
        isJunk: filter.isJunk,
        ...(filter.reason !== undefined ? { junkReason: filter.reason } : {}),
      });
    }
  }

  const included = allFiles.filter((f) => !f.isJunk);
  const excluded = allFiles.filter((f) => f.isJunk);

  return {
    files: allFiles,
    totalFiles: allFiles.length,
    includedFiles: included.length,
    excludedFiles: excluded.length,
    totalBytes: allFiles.reduce((acc, f) => acc + f.sizeBytes, 0),
    includedBytes: included.reduce((acc, f) => acc + f.sizeBytes, 0),
    savedBytes: excluded.reduce((acc, f) => acc + f.sizeBytes, 0),
  };
}

async function traverseEntry(
  entry: FileSystemEntry,
  basePath: string,
  results: UploadPlanFile[]
): Promise<void> {
  const relativePath = basePath ? `${basePath}/${entry.name}` : entry.name;
  const filterResult = shouldExclude(relativePath);

  if (filterResult.isJunk) {
    // Mark the directory itself as junk (don't recurse further)
    if (entry.isDirectory) {
      results.push({
        file: new File([], entry.name),
        relativePath,
        sizeBytes: 0,
        isJunk: true,
        ...(filterResult.reason !== undefined ? { junkReason: filterResult.reason } : {}),
      });
    }
    return;
  }

  if (entry.isFile) {
    const file = await getFileFromEntry(entry as FileSystemFileEntry);
    results.push({
      file,
      relativePath,
      sizeBytes: file.size,
      isJunk: false,
    });
  } else if (entry.isDirectory) {
    const dirEntry = entry as FileSystemDirectoryEntry;
    const children = await readDirectoryEntries(dirEntry);
    for (const child of children) {
      await traverseEntry(child, relativePath, results);
    }
  }
}

function getFileFromEntry(entry: FileSystemFileEntry): Promise<File> {
  return new Promise((resolve, reject) => entry.file(resolve, reject));
}

function readDirectoryEntries(
  dir: FileSystemDirectoryEntry
): Promise<FileSystemEntry[]> {
  return new Promise((resolve, reject) => {
    const reader = dir.createReader();
    const allEntries: FileSystemEntry[] = [];

    function readBatch(): void {
      reader.readEntries((entries) => {
        if (entries.length === 0) {
          resolve(allEntries);
        } else {
          allEntries.push(...entries);
          readBatch();
        }
      }, reject);
    }

    readBatch();
  });
}
